import { ErrorCodes } from '@/assets/composables/types'

export interface FailureContext {
  hasValidSession?: boolean
}

export const failureDetails = (error: unknown) => {
  const failure = error as {
    name?: string
    code?: string
    status?: number
    response?: { status?: number; _data?: { code?: string } }
  } | null
  return {
    name: failure?.name,
    code: failure?.response?._data?.code ?? failure?.code,
    status: failure?.response?.status ?? failure?.status,
  }
}

// Explicit application outcomes, not a blanket exclusion of all HTTP 4xx.
// Unknown failures remain diagnostic until their business meaning is defined.
const expectedCodes = new Set<string>([
  ErrorCodes.noAvailableSlots,
  ErrorCodes.sameTimeReservation,
  ErrorCodes.reservationsNotAllowed,
  ErrorCodes.reservationsLimitReached,
  ErrorCodes.resInPast,
  ErrorCodes.resInFuture,
  ErrorCodes.todayAlldayExits,
  ErrorCodes.badPartOfDayToday,
  ErrorCodes.vehicleMustExist,
  ErrorCodes.ecvAlreadyInUse,
  ErrorCodes.dateInPast,
  ErrorCodes.dateTooFar,
  ErrorCodes.missingCardReturned,
  ErrorCodes.missingSpecification,
  ErrorCodes.roleConflict,
  ErrorCodes.permissionDenied,
  ErrorCodes.userBanned,
  // Ledger ownership/not-found and assurance-policy rejections are defined
  // business/security outcomes. HTTP 5xx still overrides this exclusion below.
  ErrorCodes.cardLoanNotFound,
  ErrorCodes.invalidCardLoanId,
  ErrorCodes.cardReturnVerificationRequired,
  'invalid_credentials',
  'email_not_confirmed',
  'email_address_invalid',
  'user_banned',
  'user_not_found',
  'session_not_found',
  'refresh_token_not_found',
  'refresh_token_already_used',
  'flow_state_expired',
  'otp_expired',
  'over_request_rate_limit',
  'over_email_send_rate_limit',
  'over_sms_send_rate_limit',
  'RATE_LIMIT_EXCEEDED',
  'weak_password',
  'same_password',
])

const expectedNames = new Set(['AuthInvalidCredentialsError', 'AuthSessionMissingError'])

export const isAmbiguousAuthCallback = (error: unknown): boolean => {
  const { name, code } = failureDetails(error)
  return (
    name === 'AuthPKCECodeVerifierMissingError' ||
    code === 'flow_state_not_found' ||
    code === 'bad_code_verifier'
  )
}

export const isExpectedFailure = (error: unknown, context: FailureContext = {}): boolean => {
  const { name, code, status } = failureDetails(error)
  // A server failure is technical even if its response accidentally carries a
  // familiar business code. A bare 401/403 is also kept for investigation.
  if (status && status >= 500) return false
  // Lost PKCE state on a first login is a technical failure. With an existing
  // valid session the same rejection can be a harmless replayed callback.
  if (isAmbiguousAuthCallback(error)) return context.hasValidSession === true
  return (
    status === 429 || !!(code && expectedCodes.has(code)) || !!(name && expectedNames.has(name))
  )
}
